"use client";

import * as React from "react";
import { cn } from "../../../lib/utils";

// Context for managing drawer state
interface DrawerContextValue {
  open: boolean;
  onClose: () => void;
}

const DrawerContext = React.createContext<DrawerContextValue | undefined>(undefined);

function useDrawer() {
  const context = React.useContext(DrawerContext);
  if (!context) {
    throw new Error("Drawer components must be used within a Drawer");
  }
  return context;
}

// Root Drawer component
interface DrawerProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

function Drawer({ open, onClose, children }: DrawerProps) {
  if (!open) return null;

  return (
    <DrawerContext.Provider value={{ open, onClose }}>
      {children}
    </DrawerContext.Provider>
  );
}

// Overlay backdrop component
interface DrawerOverlayProps {
  className?: string;
}

function DrawerOverlay({ className }: DrawerOverlayProps) {
  const { onClose } = useDrawer();

  return (
    <div
      data-slot="drawer-overlay"
      className={cn(
        "fixed inset-0 z-panel",
        className
      )}
      onClick={onClose}
    />
  );
}

// Main content container
interface DrawerContentProps {
  position?: "left" | "right" | "top" | "bottom";
  className?: string;
  children: React.ReactNode;
}

function DrawerContent({
  position = "right",
  className,
  children,
}: DrawerContentProps) {
  const positionClasses = {
    left: "left-0",
    right: "right-0",
    top: "top-0",
    bottom: "bottom-0",
  };

  return (
    <div
      data-slot="drawer-content"
      className={cn(
        "fixed z-panel bg-white rounded-md",
        "[box-shadow:0_4px_12px_rgba(0,0,0,0.15)]",
        "flex flex-col box-border overflow-hidden",
        "transition-transform duration-300 ease-in-out",
        positionClasses[position],
        className
      )}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>
  );
}

// Header section
interface DrawerHeaderProps {
  className?: string;
  children: React.ReactNode;
}

function DrawerHeader({ className, children }: DrawerHeaderProps) {
  return (
    <div
      data-slot="drawer-header"
      className={cn(
        "flex-shrink-0",
        className
      )}
    >
      {children}
    </div>
  );
}

// Scrollable body section
interface DrawerBodyProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
  children: React.ReactNode;
}

function DrawerBody({ className, children, ...props }: DrawerBodyProps) {
  return (
    <div
      {...props}
      data-slot="drawer-body"
      className={cn(
        "flex-grow overflow-y-auto",
        // Custom scrollbar styling
        "[&::-webkit-scrollbar]:w-1.5",
        "[&::-webkit-scrollbar-track]:bg-gray-100",
        "[&::-webkit-scrollbar-track]:rounded-sm",
        "[&::-webkit-scrollbar-thumb]:bg-gray-400",
        "[&::-webkit-scrollbar-thumb]:rounded-sm",
        "[&::-webkit-scrollbar-thumb:hover]:bg-gray-500",
        className
      )}
    >
      {children}
    </div>
  );
}

// Footer section
interface DrawerFooterProps {
  className?: string;
  children: React.ReactNode;
}

function DrawerFooter({ className, children }: DrawerFooterProps) {
  return (
    <div
      data-slot="drawer-footer"
      className={cn(
        "flex-shrink-0",
        className
      )}
    >
      {children}
    </div>
  );
}

export {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
};
